import React, { useCallback, useEffect } from 'react';
import { connect } from 'react-redux';
import cn from 'classnames';
import { bindActionCreators } from 'redux';

import bePutSendSms from 'actions/salon/bePutSendSms.action';
import beGetClientSmsText from 'actions/sms/beGetClientSmsText.action';
import bePutClientSmsText from 'actions/sms/bePutClientSmsText.action';
import bePutSalonFlagSettings from 'actions/sms/bePutSalonFlagSettings.action';
import IMAGE_EYE from 'assets/images/icons/eye-black.svg';
import IMAGE_INFO from 'assets/images/icons/info-circle-white.svg';
import { fromAnyToMessageCount } from 'bl/messages/conversion.bl';
import { messageLinkInfo, toggleMessageLink } from 'bl/messages/manipulation.bl';
import SalonFlag from 'components/core/SalonFlag.component';
import SalonModal from 'components/dialog/SalonModal.component';
import Button from 'components/form/Button.component';
import Note from 'components/form/Note.component';
import Toggler from 'components/form/Toggler.component';
import Badge from 'components/generic/Badge.component';
import Icon from 'components/generic/Icon.component';
import OverlaySpinner from 'components/generic/OverlaySpinner.component';
import Space from 'components/generic/Space.component';
import Tooltip from 'components/generic/Tooltip.component';
import CharacterLimit from 'components/messages/CharacterLimit.component';
import CurrencySign from 'components/price/CurrencySign.component';
import WidgetSmsTemplates from 'components/widget/WidgetSmsTemplates.component';
import WidgetTiming from 'components/widget/WidgetTiming.component';
import SMS_TEMPLATE from 'constants/salon/smsTemplate.const';
import SMS_TEMPLATE_NAME from 'constants/salon/smsTemplateName.const';
import TIMING_BEFORE from 'constants/salon/timingBefore.const';
import TRACK_EVENT_TYPE from 'constants/salon/trackEventType.const';
import SF from 'constants/salonFlag.const';
import S from 'constants/string.const';
import useAef from 'hooks/useAef.hook';
import useAfn from 'hooks/useAfn.hook';
import useIsFromMobileApp from 'hooks/useIsFromMobileApp.hook';
import useResettingState from 'hooks/useResettingState.hook';
import useResettingTip from 'hooks/useResettingTip.hook';
import useStateWhileMounted from 'hooks/useStateWhileMounted.hook';
import useTip from 'hooks/useTip.hook';
import { useTranslation } from 'providers/GILT.provider';
import { withTheme } from 'providers/Theme.provider';
import {
  selectClientCommsSmsAfterReminder,
  selectClientCommsSmsSelectTemplate,
  selectClientCommsSmsTextConsultation,
  selectClientCommsSmsTextCustom,
  selectClientCommsSmsTextParking,
  selectClientCommsSmsTextPolicy,
} from 'selectors/clientCommunications.selectors';
import {
  selectCurrentSalonConsultationLink,
  selectCurrentSalonId,
  selectCurrentSalonName,
  selectCurrentSalonPhone,
  selectCurrentSalonSendSms,
  selectCurrentSalonSmsCanceling,
  selectCurrentSalonSmsOffset,
  selectCurrentSalonSmsTiming,
  selectCurrentSalonTinyUrl,
} from 'selectors/currentSalon.selectors';
import fromAnyToString from 'utils/converters/fromAnyToString';
import logger from 'utils/logger.util';
import asObject from 'utils/objects/asObject.util';
import C from 'utils/values/constant.util';

import Confirmation from 'clientCommunications/components/Reminders/Confirmation.component';
import { trackEvent } from 'tracking/actions';

import CN from './RemindersLayout.module.scss';

const L = logger('RemindersLayout'); // eslint-disable-line no-unused-vars

const FIELD_TO_TEMPLATE = Object.freeze({
  [SMS_TEMPLATE_NAME.consultation]: 'sms_after_reminder_consultation_form_text',
  [SMS_TEMPLATE_NAME.custom]: 'sms_after_reminder_custom_text',
  [SMS_TEMPLATE_NAME.parking]: 'sms_after_reminder_parking_text',
  [SMS_TEMPLATE_NAME.policy]: 'sms_after_reminder_cancellation_policy_text',
});

const TIMING_INFO_MAP = C({
  [TIMING_BEFORE.sameDay]: {
    id: TIMING_BEFORE.sameDay,
    descriptionTransKey: 'composite.widgetTiming.sameDay',
    value: '00:00:00',
  },
  [TIMING_BEFORE.dayBefore]: {
    id: TIMING_BEFORE.dayBefore,
    descriptionTransKey: 'common.hours.24h',
    value: '24:00:00',
  },
  [TIMING_BEFORE.twoDaysBefore]: {
    id: TIMING_BEFORE.twoDaysBefore,
    descriptionTransKey: 'common.hours.48h',
    value: '48:00:00',
  },
  [TIMING_BEFORE.threeDaysBefore]: {
    id: TIMING_BEFORE.threeDaysBefore,
    descriptionTransKey: 'common.hours.72h',
    value: '72:00:00',
  },
});

const MAX_SMS_COUNT = 500;
const SMS_REMINDER_ENABLED_TOPUP_PICE = 30;
const SMS_REMINDER_DISABLED_TOPUP_PICE = 35;

const SMS_REMINDER_TIME_24_HOURS = 24;
const SMS_REMINDER_TIME_48_HOURS = 48;
const SMS_REMINDER_TIME_72_HOURS = 72;

const determineTimingDescription = (options) => {
  if (options?.smsTiming === TIMING_BEFORE.sameDay) return `${Math.round(options?.smsOffset / 60)} ${options.t('reminders.hours')}`;

  const info = TIMING_INFO_MAP[options?.smsTiming];
  return info ? `${options.t(info.descriptionTransKey)}` : fromAnyToString(options?.smsTiming);
};

const pad2digits = (number) => `${number < 10 ? '0' : ''}${number}`;

const determineTimingValue = (options) => {
  if (options?.smsTiming !== TIMING_BEFORE.sameDay) {
    const info = TIMING_INFO_MAP[options?.smsTiming];
    const { value } = asObject(info);

    if (value) return value;
  }

  return `${pad2digits(Math.round(options?.smsOffset / 60))}:00:00` || '00:00:00';
};

const USE_SPINNER = true;
const TEMPLATES = Object.freeze(Object.values(SMS_TEMPLATE));

const TooltipReminderText = () => {
  const { t } = useTranslation();

  return (
    <div>
      {t('reminders.badgeTooltipPart1', { maxSMS: MAX_SMS_COUNT })}
      <Space>
        <CurrencySign />
        <SalonFlag name={SF.smsPricing}>
          <SalonFlag.IfDisabled>{SMS_REMINDER_DISABLED_TOPUP_PICE}</SalonFlag.IfDisabled>
          <SalonFlag.IfEnabled>{SMS_REMINDER_ENABLED_TOPUP_PICE}</SalonFlag.IfEnabled>
        </SalonFlag>
        {t('reminders.badgeTooltipPart2')}
      </Space>
      {t('reminders.badgeTooltipPart3')}
    </div>
  );
};

const fromAnyToDisplayTemplate = (value) => fromAnyToString(value).replace(/_/g, ' ');

const RemindersLayout = ({
  beGetClientSmsText,
  bePutClientSmsText,
  bePutSendSms,
  bePutSalonFlagSettings,
  consultationLink,
  isCancelingSms,
  isDarkMode,
  isSendSms,
  salonId,
  salonName,
  salonPhone,
  smsAfterReminder,
  smsOffset,
  smsSelectedTemplate,
  smsTextConsultation,
  smsTextCustom,
  smsTextParking,
  smsTextPolicy,
  smsTiming,
  tinyUrl,
}) => {
  const { t } = useTranslation();
  const isFromMobile = useIsFromMobileApp();

  const { val: isBoltonOn, set: setBolton } = useResettingTip(smsAfterReminder, useResettingState.NIL);
  const { val: isCancellingSmsOn, set: setCancelingSms } = useTip(isCancelingSms);
  const { val: isReminderOn, set: setReminder } = useTip(isSendSms);
  const { val: isTemplateEditOpen, yay: openTemplateEdit, nay: closeTemplateEdit } = useTip(false);

  const displayHours = determineTimingDescription({ smsTiming, smsOffset, t });
  const widgetValue = determineTimingValue({ smsTiming, smsOffset });

  const [smsTextPerTemplate, setSmsTextPerTemplate] = useStateWhileMounted({
    [SMS_TEMPLATE_NAME.consultation]: smsTextConsultation,
    [SMS_TEMPLATE_NAME.custom]: smsTextCustom,
    [SMS_TEMPLATE_NAME.parking]: smsTextParking,
    [SMS_TEMPLATE_NAME.policy]: smsTextPolicy,
  });

  const smsInfoResult = useAef(async () => (await beGetClientSmsText({ salonId })).json, [beGetClientSmsText, salonId]);
  const smsInfo = asObject(asObject(smsInfoResult).value);

  // NOTE: selector is not added to the salon API
  const [smsTemplateName, setTemplateName] = useStateWhileMounted(smsSelectedTemplate);
  const currentTemplate = smsTemplateName || smsInfo.selected_template_sms_after_reminder;
  const displayTemplate = fromAnyToDisplayTemplate(currentTemplate);
  const [currentText, setCurrentText] = useStateWhileMounted(smsTextPerTemplate[currentTemplate]);
  const currentField = FIELD_TO_TEMPLATE[currentTemplate];

  const [selectionBgn, setSelectionBgn] = useStateWhileMounted();
  const [selectionEnd, setSelectionEnd] = useStateWhileMounted();

  const { isLinkProvided: isConsProvided, isInserted: isConsInserted } = messageLinkInfo({
    type: 'consultation',
    link: consultationLink,
    text: currentText,
  });

  const { isLinkProvided: isNcobProvided, isInserted: isNcobInserted } = messageLinkInfo({
    type: 'ncob',
    link: tinyUrl,
    text: currentText,
  });

  const handleNcobLink = useCallback(() => {
    const { text: modifiedText } = toggleMessageLink({
      type: 'ncob',
      link: tinyUrl,
      text: currentText,
      bgn: selectionBgn,
      end: selectionEnd,
      separator: S.space,
    });

    setCurrentText(modifiedText);
    setSmsTextPerTemplate({ ...smsTextPerTemplate, [currentTemplate]: modifiedText });
  }, [tinyUrl, currentText, selectionBgn, selectionEnd, setCurrentText, setSmsTextPerTemplate, smsTextPerTemplate, currentTemplate]);

  const handleConsLink = useCallback(() => {
    const { text: modifiedText } = toggleMessageLink({
      type: 'consultation',
      link: consultationLink,
      text: currentText,
      bgn: selectionBgn,
      end: selectionEnd,
      separator: S.space,
    });

    setCurrentText(modifiedText);
    setSmsTextPerTemplate({ ...smsTextPerTemplate, [currentTemplate]: modifiedText });
  }, [consultationLink, currentText, selectionBgn, selectionEnd, setCurrentText, setSmsTextPerTemplate, smsTextPerTemplate, currentTemplate]);

  const handleTemplateTextChange = useCallback(
    ({ raw }) => {
      const value = fromAnyToString(raw);
      setCurrentText(value);
      setSmsTextPerTemplate({ ...smsTextPerTemplate, [currentTemplate]: value });
    },
    [currentTemplate, setCurrentText, setSmsTextPerTemplate, smsTextPerTemplate],
  );

  const handleTemplateSelectionChange = useCallback(
    ({ raw }) => {
      const { bgn, end } = asObject(raw);
      setSelectionBgn(bgn);
      setSelectionEnd(end);
    },
    [setSelectionBgn, setSelectionEnd],
  );

  const [saveResult, handleSaveTemplate] = useAfn(
    async (ev) => {
      const result = await bePutClientSmsText({ salonId, [currentField]: currentText });

      if (!result.is.ok) {
        L.warn('handleSaveTemplate()', 'result:', result, 'ev:', ev);
      }

      const value = asObject(result.json)[currentField];
      setSmsTextPerTemplate({ ...smsTextPerTemplate, [currentTemplate]: value });
      closeTemplateEdit();
    },
    [bePutClientSmsText, salonId, currentField, currentText, setSmsTextPerTemplate, smsTextPerTemplate, currentTemplate, closeTemplateEdit],
  );

  const [saveSmsTimingResult, handleSaveSmsTiming] = useAfn(
    async (ev) => {
      if (!ev.raw) {
        L.warn('handleSaveSmsTiming()', 'Invalid ev.raw:', ev.raw);
        return;
      }

      const timing = ev.raw.before;
      const data = { sms_timing: timing };
      const result = await bePutSalonFlagSettings({ data });

      if (!result.is.ok) L.warn('handleSaveSmsTiming()', 'result:', result);
    },
    [bePutSalonFlagSettings],
  );

  const [saveSmsOffsetResult, handleSaveSmsOffset] = useAfn(
    async (ev) => {
      if (!ev.raw) {
        L.warn('handleChangeSmsTiming()', 'Invalid ev.raw:', ev.raw);
        return;
      }

      const offset = ev.raw.int * 60 || 0;
      const data = { sms_timing: TIMING_BEFORE.sameDay, sms_offset: offset };
      const result = await bePutSalonFlagSettings({ data });

      if (!result.is.ok) L.warn('handleChangeSmsTiming()', 'warn:', result);
    },
    [bePutSalonFlagSettings],
  );

  // on select template update flag in api smstext -> selected_template_sms_after_reminder with selected template name
  const [selectTemplateNameResult, handleSelectTemplateName] = useAfn(
    async ({ raw }) => {
      const selectedId = asObject(raw).id || SMS_TEMPLATE_NAME.policy;

      const result = await bePutClientSmsText({ salonId, selected_template_sms_after_reminder: selectedId });

      if (!result.is.ok) {
        L.warn('handleSelectTemplateName()', 'result:', result);
        return selectedId;
      }

      const savedId = asObject(result.json).selected_template_sms_after_reminder;
      setTemplateName(savedId);

      return savedId;
    },
    [bePutClientSmsText, salonId, setTemplateName],
  );

  const [sendSmsResult, handleSendSms] = useAfn(async () => {
    const resultAppointment = await bePutSendSms({ salonId, send_sms: !isReminderOn });
    if (!resultAppointment.is.ok) {
      L.warn('handleSendSms()', 'resultAppointment:', resultAppointment);
      return;
    }
    const value = asObject(resultAppointment.json).send_sms;
    setReminder(value);
    trackEvent(TRACK_EVENT_TYPE.enabledReminders);

    // NOTE: if send_sms is false then auto disable enable_sms_after_reminder, otherwise abort
    if (value) return;

    const resultAfter = await bePutClientSmsText({ salonId, enable_sms_after_reminder: false });
    if (!resultAfter.is.ok) {
      L.warn('handleSendSmsAfterReminder()', 'resultAfter:', resultAfter);
      return;
    }
    setBolton(asObject(resultAfter.json).enable_sms_after_reminder);
    trackEvent(TRACK_EVENT_TYPE.enabledBoltOn);
  }, [bePutSendSms, salonId, isReminderOn, setReminder, bePutClientSmsText, setBolton]);

  const [sendSmsAfterReminderResult, handleSendSmsAfterReminder] = useAfn(async () => {
    const result = await bePutClientSmsText({ salonId, enable_sms_after_reminder: !isBoltonOn });

    if (!result.is.ok) {
      L.warn('handleSendSmsAfterReminder()', 'result:', result);
      return;
    }

    const value = asObject(result.json).enable_sms_after_reminder;
    setBolton(value);
    trackEvent(TRACK_EVENT_TYPE.enabledBoltOn);

    // NOTE: if enable_sms_after_reminder is true then auto enable send_sms, otherwise abort
    if (!value) return;

    const resultAppointment = await bePutSendSms({ salonId, send_sms: true });
    if (!resultAppointment.is.ok) {
      L.warn('handleSendSms()', 'resultAppointment:', resultAppointment);
      return;
    }
    setReminder(asObject(resultAppointment.json).send_sms);
    trackEvent(TRACK_EVENT_TYPE.enabledReminders);
  }, [bePutClientSmsText, salonId, isBoltonOn, setBolton, bePutSendSms, setReminder]);

  const [cancelingSmsResult, handleCancelingSms] = useAfn(async () => {
    const result = await bePutSalonFlagSettings({ data: { cancel_via_sms: !isCancellingSmsOn } });

    if (!result.is.ok) {
      L.warn('cancelingSmsResult()', 'result:', result);
      return result;
    }

    setCancelingSms(asObject(result.json).cancel_via_sms);

    return result;
  }, [bePutSalonFlagSettings, isCancellingSmsOn, setCancelingSms]);

  useEffect(
    () =>
      void setSmsTextPerTemplate({
        [SMS_TEMPLATE_NAME.consultation]: smsTextConsultation,
        [SMS_TEMPLATE_NAME.custom]: smsTextCustom,
        [SMS_TEMPLATE_NAME.parking]: smsTextParking,
        [SMS_TEMPLATE_NAME.policy]: smsTextPolicy,
      }),
    [setSmsTextPerTemplate, smsTextConsultation, smsTextCustom, smsTextParking, smsTextPolicy],
  );

  useEffect(
    //
    () => void setCurrentText(smsTextPerTemplate[currentTemplate]),
    [currentTemplate, setCurrentText, smsTextPerTemplate],
  );

  const messagesUsed = fromAnyToMessageCount(currentText);

  const isSpinnerRendered =
    (USE_SPINNER && (sendSmsResult.isLoading || saveResult.isLoading)) ||
    saveSmsTimingResult.isLoading ||
    saveSmsOffsetResult.isLoading ||
    cancelingSmsResult.isLoading ||
    sendSmsAfterReminderResult.isLoading ||
    selectTemplateNameResult.isLoading;

  if (
    sendSmsResult.error ||
    saveResult.error ||
    saveSmsTimingResult.error ||
    saveSmsOffsetResult.error ||
    cancelingSmsResult.error ||
    sendSmsAfterReminderResult.error ||
    selectTemplateNameResult.error
  ) {
    L.warn('()', {
      sendSmsResult,
      saveResult,
      saveSmsTimingResult,
      saveSmsOffsetResult,
      cancelingSmsResult,
      sendSmsAfterReminderResult,
      selectTemplateNameResult,
    });
  }

  return (
    <div
      data-bem="RemindersLayout"
      className={cn({
        [CN.component]: true,
        [CN.darkMode]: isDarkMode,
      })}
    >
      {isSpinnerRendered && <OverlaySpinner />}
      <div className={CN.wraper}>
        <Confirmation tag="RemindersLayout" />
        <div data-bem="RemindersLayout__sent-before" className={CN.container}>
          <div data-bem="RemindersLayout__sent-before-flexer" className={CN.panel}>
            <div data-bem="RemindersLayout__settings" className={CN.contentLeft}>
              <div>
                <div className={CN.gap}>
                  <div className={CN.containerLabelScissors}>
                    <div>
                      {t('reminders.titlePart1')} <span className={CN.bold}> {t('reminders.titlePart2')} </span> {t('reminders.titlePart3')}
                    </div>
                  </div>

                  <div className={CN.header}>
                    <div className={CN.panelTitle}>{t('reminders.section1Title')}</div>

                    <div className={CN.controls}>
                      <div className={CN.badgeContainer}>
                        <Badge tag="ReminderLayout__reminder--unlimited" name={t('composite.badge.unlimited')} variant="unlimited" />
                        <Badge tag="ReminderLayout__reminder--included" name={t('composite.badge.includeInPlan')} variant="fine" />
                      </div>
                      <Toggler tag="ReminderLayout__reminder-toggle" onClick={handleSendSms} selected={isReminderOn} />
                    </div>
                  </div>

                  <div className={CN.text}>
                    <div>
                      {t('reminders.illustrativeTextPart1', { salon: salonName })}
                      {isCancellingSmsOn ? t('reminders.illustrativeTextPart2') : t('reminders.illustrativeTextPart3', { salonPhone })}
                    </div>
                    <br />
                    <span className={CN.dataTime}>{t('reminders.examplesText')}</span>
                  </div>
                </div>

                <div data-bem="ReminderLayout__bolt-on-section" className={CN.separator}>
                  <div className={CN.header}>
                    <div className={CN.panelTitle}>{t('reminders.section2Title')}</div>
                    <div className={CN.controls}>
                      <Badge
                        tag="ReminderLayout__bolton"
                        name={
                          isBoltonOn
                            ? isFromMobile
                              ? t('composite.badge.addOnEnabledMobile')
                              : t('composite.badge.addOnEnabled')
                            : isFromMobile
                              ? t('composite.badge.addOnDisabledMobile')
                              : t('composite.badge.addOnDisabled')
                        }
                        variant={isBoltonOn ? 'success' : 'draft'}
                      >
                        {!isFromMobile && (
                          <Tooltip content={<TooltipReminderText />} variant="top">
                            <img
                              src={IMAGE_INFO}
                              alt="Tooltip icon"
                              className={cn({
                                [CN.icoInfo]: true,
                                [CN.inactive]: !isBoltonOn,
                              })}
                            />
                          </Tooltip>
                        )}
                      </Badge>
                      <Toggler tag="ReminderLayout__bolton-toggle" onClick={handleSendSmsAfterReminder} selected={isBoltonOn} />
                    </div>
                  </div>
                  {!isFromMobile && (
                    <div className={CN.text}>
                      <div>
                        {t('reminders.section2DescriptionPart1', { maxSMS: MAX_SMS_COUNT })} <CurrencySign />
                        <SalonFlag name={SF.smsPricing}>
                          <SalonFlag.IfDisabled>{SMS_REMINDER_DISABLED_TOPUP_PICE}</SalonFlag.IfDisabled>
                          <SalonFlag.IfEnabled>{SMS_REMINDER_ENABLED_TOPUP_PICE}</SalonFlag.IfEnabled>
                        </SalonFlag>
                        {S.space}
                        {t('reminders.section2DescriptionPart2')}
                      </div>
                    </div>
                  )}
                  <div className={CN.flexTitle}>
                    <div className={CN.semi}>{t('reminders.section2SubTitle')}</div>
                    <div className={CN.textBoltOn}>
                      {t('reminders.textUsed')}
                      <strong className={CN.bold}>{messagesUsed}</strong>
                    </div>
                  </div>

                  <WidgetSmsTemplates onChange={handleSelectTemplateName} onEdit={openTemplateEdit} options={TEMPLATES} tag="RemindersLayout" value={currentTemplate} />
                </div>
              </div>
            </div>
            <div data-bem="RemindersLayout__preview" className={CN.previewPreview}>
              <div className={CN.previewContainer}>
                <div className={CN.previewTitle}>
                  <img src={IMAGE_EYE} alt="Icon eye preview" className={CN.icoEye} />
                  {t('reminders.section3Title')}
                </div>
                <div className={CN.phoneContainer}>
                  <div className={CN.appointmentText}>
                    {displayHours} {t('reminders.beforeAppointment')}
                  </div>
                  <div className={CN.draftContainer}>
                    {!isReminderOn ? (
                      <Badge variant="draft">
                        <span className={CN.badgeFont}>{t('reminders.draftReminderOff')}</span>
                      </Badge>
                    ) : (
                      <Badge variant="outline">
                        <span className={CN.badgeFont}>{t('reminders.reminder')}</span>
                      </Badge>
                    )}
                  </div>
                  <div
                    className={cn({
                      [CN.areaDraft]: true,
                      [CN.selected]: isReminderOn,
                    })}
                  >
                    <div>
                      {t('reminders.illustrativeTextPart1', { salon: salonName })} {isCancellingSmsOn ? t('reminders.illustrativeTextPart2') : t('reminders.illustrativeTextPart3', { salonPhone })}
                    </div>
                  </div>
                  <div className={CN.draftContainer}>
                    {!isBoltonOn ? (
                      <Badge variant="draft">
                        <span className={CN.badgeFont}>{t('reminders.draftBoltOff')}</span>
                      </Badge>
                    ) : (
                      <Badge variant="outline">
                        <span className={CN.badgeFont}>{t('reminders.boltOn')}</span>
                      </Badge>
                    )}
                  </div>
                  <div
                    className={cn({
                      [CN.areaDraft]: true,
                      [CN.selected]: isBoltonOn,
                    })}
                  >
                    <div>{currentText}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div data-bem="RemindersLayout__when-is-sent" className={CN.outerGap}>
            <div className={CN.panel}>
              <div className={CN.settingsContainer}>
                <div className={CN.containerLabelClock}>
                  <div>
                    <span className={CN.bold}> {t('reminders.title2Part1')} </span> {t('reminders.title2Part2')}
                  </div>
                </div>
                <div className={CN.settingsTiming}>
                  <div className={CN.header}>
                    <div className={CN.panelTitle}>{t('reminders.section4Title')}</div>
                  </div>
                  <div className={CN.text}>
                    <div className={CN.semi}>{t('reminders.section4Subtitle1', { hours: displayHours })}</div>
                  </div>
                  <div className={cn(CN.text, CN.bold, CN.smsTiming)}>
                    <WidgetTiming name="repeat" tag="RemindersLayout" onChangeTiming={handleSaveSmsTiming} onChangeHours={handleSaveSmsOffset} value={widgetValue} />
                  </div>
                  <div className={CN.text}>
                    <div className={CN.bold}>{t('reminders.section4Subtitle2')}</div>
                    <div>{t('reminders.section4Description')}</div>
                  </div>
                  <div className={CN.togglerContainer}>
                    <Toggler onClick={handleCancelingSms} selected={isCancellingSmsOn} />
                  </div>
                </div>
                <div className={CN.settingsTips}>
                  <div className={CN.tipsTitle}>
                    <div className={CN.icoStar}>{S.star}</div>
                    {t('reminders.section5Title')}
                  </div>
                  <div className={CN.tipsContainer}>
                    <span>{t('reminders.section5Description')}</span>
                    <div className={CN.tipTitle}>
                      {t('reminders.section5SubtitlePart1')}
                      <span className={CN.purple}>
                        <Space>{SMS_REMINDER_TIME_24_HOURS}</Space>
                      </span>
                      {t('reminders.section5SubtitlePart2')}
                      <span className={CN.blue}>
                        <Space>{SMS_REMINDER_TIME_48_HOURS}</Space>
                      </span>
                      {t('reminders.hours')}
                    </div>
                    <div className={CN.tip}>{t('reminders.section5Bullet1')}</div>
                    <div className={CN.tip}>{t('reminders.section5Bullet2')}</div>
                    <div className={CN.tipTitle}>
                      {t('reminders.section5SubtitlePart1')}
                      <span className={CN.blue}>
                        <Space>{SMS_REMINDER_TIME_48_HOURS}</Space>
                      </span>
                      {t('reminders.section5SubtitlePart2')}
                      <span className={CN.green}>
                        <Space>{SMS_REMINDER_TIME_72_HOURS}</Space>
                      </span>
                      {t('reminders.hours')}
                    </div>
                    <div className={CN.tip}>{t('reminders.section5Bullet1')}</div>
                    <div className={CN.tip}>{t('reminders.section5Bullet2')}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className={CN.gapBottom} />
        </div>
      </div>

      <SalonModal tag="RemindersLayout__templates" isOpen={isTemplateEditOpen} variant="large" withFooter>
        <SalonModal.Header>
          {t('reminders.cancellationModal.title', { template: displayTemplate })}
          <Button variant="empty" onClick={closeTemplateEdit}>
            <Icon variant="close" />
          </Button>
        </SalonModal.Header>
        <SalonModal.Body>
          <div className={CN.textAreaContainer}>
            <Note
              autoscroll
              hasExternalValue
              name="note"
              onChange={handleTemplateTextChange}
              onSelectionChange={handleTemplateSelectionChange}
              tag="RemindersLayout__note"
              value={currentText}
              variant="expandedForce"
            />
            <small className={CN.textAreaInfo}>
              <CharacterLimit value={currentText} />
            </small>
          </div>
        </SalonModal.Body>
        <SalonModal.Footer>
          <div className={CN.modalButtons}>
            <div className={CN.modalFooterInfo}>
              <Tooltip content={t('reminders.cancellationModal.tooltipText')} variant="top">
                {t('reminders.cancellationModal.infoText')} <span className={CN.bold}>{messagesUsed}</span>
                <img src={IMAGE_INFO} alt="Tooltip icon" className={CN.icoInfoModal} />
              </Tooltip>
            </div>

            {isConsProvided && (
              <Button tag="RemindersLayout__link-consultation" variant="outlineForce" onClick={handleConsLink}>
                {isConsInserted ? t('reminders.cancellationModal.removeConsultLinkBtn') : t('reminders.cancellationModal.addConsultLinkBtn')}
              </Button>
            )}

            {isNcobProvided && (
              <Button tag="RemindersLayout__link-booking" variant="outlineForce" onClick={handleNcobLink}>
                {isNcobInserted ? t('reminders.cancellationModal.removeBookingLinkBtn') : t('reminders.cancellationModal.addBookingLinkBtn')}
              </Button>
            )}

            <Button variant="primaryForce" casing="dialog" onClick={handleSaveTemplate}>
              {t('common.buttons.save')}
            </Button>
          </div>
        </SalonModal.Footer>
      </SalonModal>
    </div>
  );
};

const mapStateToProps = (state) => ({
  consultationLink: selectCurrentSalonConsultationLink(state),
  isCancelingSms: selectCurrentSalonSmsCanceling(state),
  isSendSms: selectCurrentSalonSendSms(state),
  salonId: selectCurrentSalonId(state),
  salonName: selectCurrentSalonName(state),
  salonPhone: selectCurrentSalonPhone(state),
  smsAfterReminder: selectClientCommsSmsAfterReminder(state),
  smsOffset: selectCurrentSalonSmsOffset(state),
  smsSelectedTemplate: selectClientCommsSmsSelectTemplate(state),
  smsTextConsultation: selectClientCommsSmsTextConsultation(state),
  smsTextCustom: selectClientCommsSmsTextCustom(state),
  smsTextParking: selectClientCommsSmsTextParking(state),
  smsTextPolicy: selectClientCommsSmsTextPolicy(state),
  smsTiming: selectCurrentSalonSmsTiming(state),
  tinyUrl: selectCurrentSalonTinyUrl(state),
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      beGetClientSmsText,
      bePutClientSmsText,
      bePutSalonFlagSettings,
      bePutSendSms,
    },
    dispatch,
  );

const RemindersLayoutHoc = connect(mapStateToProps, mapDispatchToProps)(withTheme()(RemindersLayout));

export default RemindersLayoutHoc;
